export const BACKEND_RICH_TEXT_TEXT_GRAY = "#b9c7d3";

const DARK_COLOR_NAMES = new Set(["black"]);
const DARK_LUMINANCE_LIMIT = 72;

const stripImportant = (value) => String(value || "").replace(/\s*!important\s*$/i, "").trim();

const parseHexColor = (value) => {
  const hex = value.replace("#", "").trim();
  if (![3, 4, 6, 8].includes(hex.length)) return null;

  const chars = hex.length === 3 || hex.length === 4
    ? hex.slice(0, 3).split("").map((item) => `${item}${item}`)
    : [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)];

  const channels = chars.map((item) => Number.parseInt(item, 16));
  return channels.every((channel) => Number.isFinite(channel)) ? channels : null;
};

const parseRgbColor = (value) => {
  const match = value.match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return null;
  const channels = match[1]
    .split(",")
    .slice(0, 3)
    .map((item) => Number.parseFloat(item.trim()));

  return channels.length === 3 && channels.every((channel) => Number.isFinite(channel))
    ? channels
    : null;
};

const parseHslLightness = (value) => {
  const match = value.match(/^hsla?\(([^)]+)\)$/i);
  if (!match) return null;
  const parts = match[1].split(",").map((item) => item.trim());
  const lightness = Number.parseFloat(parts[2]);
  return Number.isFinite(lightness) ? lightness : null;
};

export const isDarkTextColor = (value) => {
  const clean = stripImportant(value).toLowerCase();
  if (!clean) return false;
  if (DARK_COLOR_NAMES.has(clean)) return true;

  if (clean.startsWith("#")) {
    const channels = parseHexColor(clean);
    if (!channels) return false;
    return Math.max(...channels) <= DARK_LUMINANCE_LIMIT;
  }

  const rgbChannels = parseRgbColor(clean);
  if (rgbChannels) return Math.max(...rgbChannels) <= DARK_LUMINANCE_LIMIT;

  const lightness = parseHslLightness(clean);
  if (lightness !== null) return lightness <= 24;

  return false;
};

export const normalizeBackendTextColor = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  const important = /\s*!important\s*$/i.test(raw) ? " !important" : "";
  return isDarkTextColor(raw) ? `${BACKEND_RICH_TEXT_TEXT_GRAY}${important}` : raw;
};

export const normalizeStyleTextColors = (style = "") =>
  String(style)
    .split(";")
    .map((declaration) => {
      const index = declaration.indexOf(":");
      if (index === -1) return declaration.trim();

      const property = declaration.slice(0, index).trim();
      const value = declaration.slice(index + 1).trim();
      const propertyKey = property.toLowerCase();

      if (propertyKey === "color" || propertyKey === "-webkit-text-fill-color") {
        return `${property}: ${normalizeBackendTextColor(value)}`;
      }

      return `${property}: ${value}`;
    })
    .filter(Boolean)
    .join("; ");

const sanitizeRichTextWithDom = (value) => {
  const template = document.createElement("template");
  template.innerHTML = String(value || "");

  template.content
    .querySelectorAll("script, style, iframe, object, embed, link, meta")
    .forEach((node) => node.remove());

  template.content.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const attrValue = attribute.value || "";

      if (name.startsWith("on")) {
        node.removeAttribute(attribute.name);
        return;
      }

      if ((name === "href" || name === "src") && /^\s*javascript:/i.test(attrValue)) {
        node.removeAttribute(attribute.name);
        return;
      }

      if (name === "style") {
        const nextStyle = normalizeStyleTextColors(attrValue);
        if (nextStyle) node.setAttribute("style", nextStyle);
        else node.removeAttribute("style");
        return;
      }

      if (name === "color") {
        node.setAttribute(attribute.name, normalizeBackendTextColor(attrValue));
      }
    });
  });

  return template.innerHTML;
};

const sanitizeRichTextFallback = (value) =>
  String(value || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "")
    .replace(/\son\w+=(["']).*?\1/gi, "")
    .replace(/\s(href|src)=(["'])javascript:.*?\2/gi, "")
    .replace(/\sstyle=(["'])(.*?)\1/gi, (_, quote, style) => {
      const nextStyle = normalizeStyleTextColors(style);
      return nextStyle ? ` style=${quote}${nextStyle}${quote}` : "";
    })
    .replace(/\scolor=(["'])(.*?)\1/gi, (_, quote, color) => ` color=${quote}${normalizeBackendTextColor(color)}${quote}`);

export const normalizeBackendRichText = (value) => {
  if (!value) return "";
  if (typeof document !== "undefined" && document.createElement) {
    return sanitizeRichTextWithDom(value);
  }
  return sanitizeRichTextFallback(value);
};
